import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { nameValidation, statusValidation } from "validationSchemas/common";
import { UIInput, UIReactSelect } from "components/Inputfields";
import { AttributeFormCard } from "components/AddEditAttributes";
import { getAuthCookie } from "../../utils/authCookies";

export type AttributeFormValues = {
  name: string;
  parameters: string; // comma-separated
  description: string;
  statuss: string; // "Active" | "Inactive"
};
const SignageAddEditAttribute: React.FC = () => {
  const { attributeId } = useParams<{ attributeId: string }>();
  const isEdit = !!attributeId;
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Fetch existing for edit
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/product-attributes/${attributeId}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch attribute data");
        const data = await response.json();
        if (!cancelled) {
          // Prime initial values into formik below via setValues once formik exists
          setInitialFromData(data);
        }
      } catch (err: any) {
        showError(err?.message || "Error fetching attribute data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attributeId, isEdit, token]);

  // Helper state to push into formik when edit loads
  const [initialOverride, setInitialOverride] = useState<AttributeFormValues | null>(null);
  function setInitialFromData(data: any) {
    const active = (data?.status === true) || String(data?.status).toLowerCase() === "active";
    setInitialOverride({
      name: data?.name || "",
      parameters: Array.isArray(data?.values) ? data.values.join(",") : (data?.values || ""),
      description: data?.description || "",
      statuss: active ? "Active" : "Inactive",
    });
  }

  const formik = useFormik<AttributeFormValues>({
    enableReinitialize: true,
    initialValues: initialOverride || {
      name: "",
      parameters: "",
      description: "",
      statuss: "Active",
    },
    validationSchema: Yup.object({
      name: nameValidation,
      parameters: Yup.string().required("Values are required"),
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        const payload = {
          name: values.name,
          values: values.parameters.split(",").map(v => v.trim()).filter(Boolean),
          description: values.description,
          status: values.statuss === "Active",
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/product-attributes/${attributeId}`
          : `${process.env.REACT_APP_API_URL}/product-attributes`;
        const method = isEdit ? "PUT" : "POST";

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok || (result as any)?.error) {
          const message = (result as any)?.error || (result as any)?.message || "Failed to save data";
          showError(message);
          throw new Error(message);
        }
        showSuccess(isEdit ? "Attribute updated successfully!" : "Attribute added successfully!");
        navigate(`${baseRoute}/attributes`);
      } catch (err: any) {
        showError(err?.message || (isEdit ? "Failed to update attribute." : "Failed to add attribute."));
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  if (loading) return <div className="content container">Loading…</div>;

  return (
    <AttributeFormCard
      title={isEdit ? "Edit Attribute" : "Add Attribute"}
      formik={formik}
      formSubmitting={formSubmitting}
      onCancel={() => navigate(-1)}
    />
  );
};

export default SignageAddEditAttribute;